
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import axios from "axios";
import Loader from "../../../components/ui/Loader/Loader";
import TapIcon from "../../../services/TapIcon";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function AssetIotMap({ data }) {
    const [deviceData, setDeviceData] = useState(null)
    const [loading, setLoading] = useState(false)
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef({});
    const [currentEquipment, setCurrentEquipment] = useState(null);
    const [mapReady, setMapReady] = useState(false);

    const fetchDeviceData = (imeiNumber) => {
        setLoading(true)
        axios.get(`https://api6.tappetbox.in/api/DeviceData/latest/${imeiNumber}`)
            .then(response => {
                setDeviceData(response.data);
            })
            .catch(error => {
                console.error("Error fetching device data:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (data?.iot_device_imei) {
            fetchDeviceData(data?.iot_device_imei)
        }
    }, [data])

    const processEquipmentData = useCallback((device) => {
        if (!device) return null;

        let lat = device?.latitude;
        let lng = device?.longitude;
        if (!lat || !lng || lat === 0 || lng === 0 || isNaN(lat) || isNaN(lng)) {
            lat = 20.5937;
            lng = 78.9629;
        }

        return {
            id: device.imei || device.id,
            name: data ? data.name : `Device ${device.imei?.substring(8) || device.id}`,
            lat: lat,
            lng: lng,
            speed: device.speed || 0,
            ignition: device.ignition === 1,
            externalBattery: device.externalVoltage || 0,
            internalBattery: device.batteryVoltage || 0,
            lastSeen: device.serverTime || new Date().toISOString(),
            direction: device.direction || 0,
            engineStatus: device.engineStatus || false
        };
    }, [data]);

    const calculateStatus = useCallback((equipment) => {
        if (!equipment || !equipment.lastSeen) return "Not Available";

        const now = new Date();
        const lastSeen = new Date(equipment.lastSeen);
        const diffHours = (now - lastSeen) / (1000 * 60 * 60);

        if (diffHours > 12) {
            return "Not Available";
        }

        if (equipment.ignition && equipment.speed > 0) {
            return "Running";
        } else if (equipment.ignition && equipment.speed === 0) {
            return "Idle";
        } else {
            return "Stopped";
        }
    }, []);

    const equipmentData = useMemo(() => {
        if (!deviceData) {
            return [];
        }
        const processedData = processEquipmentData(deviceData);
        return processedData ? [processedData] : [];
    }, [deviceData, processEquipmentData]);

    const getStatusColor = useCallback((status) => {
        switch (status) {
            case "Running": return "#45BEA9";
            case "Idle": return "#FF9800";
            case "Stopped": return "#F44336";
            case "Not Available": return "#9E9E9E";
            default: return "#2196F3";
        }
    }, []);

    const getBatteryColor = useCallback((voltage) => {
        if (voltage >= 12) return "#45BEA9";
        if (voltage >= 11) return "#FF9800";
        return "#F44336";
    }, []);

    const createCustomIcon = useCallback((equipment) => {
        if (!equipment || !equipment.id) {
            return L.divIcon({});
        }
        const displayId = equipment.id.length > 4 ? equipment.id.substring(equipment.id.length - 4) : equipment.id;
        const status = calculateStatus(equipment);
        const color = getStatusColor(status);

        return L.divIcon({
            className: 'custom-marker',
            html: `
                <div style="
                    width: 35px;
                    height: 35px;
                    background-color: ${color};
                    border-radius: 50%;
                    border: 2px solid white;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    cursor: pointer;
                    transition: transform 0.2s;
                    font-size: 9px;
                    position: relative;
                ">
                    ${displayId}
                    <div style="
                        position: absolute;
                        bottom: -2px;
                        right: -2px;
                        width: 10px;
                        height: 10px;
                        background-color: ${equipment.ignition ? '#4CAF50' : '#F44336'};
                        border-radius: 50%;
                        border: 1px solid white;
                    "></div>
                </div>
            `,
            iconSize: [35, 35],
            iconAnchor: [17, 35],
            popupAnchor: [0, -35]
        });
    }, [calculateStatus, getStatusColor]);


    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        const initMap = () => {
            try {
                const defaultCenter = [20.5937, 78.9629];
                mapInstance.current = L.map(mapRef.current, {
                    zoomControl: true,
                    attributionControl: false
                }).setView(defaultCenter, 5);
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; OpenStreetMap contributors',
                    maxZoom: 19,
                    minZoom: 3
                }).addTo(mapInstance.current);

                setMapReady(true);

                setTimeout(() => {
                    if (mapInstance.current) {
                        mapInstance.current.invalidateSize();
                    }
                }, 100);

            } catch (error) {
                console.error("Failed to initialize map:", error);
                setMapReady(false);
            }
        };

        const timer = setTimeout(initMap, 100);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (!mapReady || !mapInstance.current || equipmentData.length === 0) return;

        try {

            Object.values(markersRef.current).forEach(marker => {
                if (marker && mapInstance.current) {
                    mapInstance.current.removeLayer(marker);
                }
            });
            markersRef.current = {};


            equipmentData.forEach(equipment => {
                if (!equipment || !equipment.lat || !equipment.lng ||
                    isNaN(equipment.lat) || isNaN(equipment.lng)) {
                    return;
                }

                const marker = L.marker([equipment.lat, equipment.lng], {
                    icon: createCustomIcon(equipment)
                }).addTo(mapInstance.current);

                marker.on('click', () => {
                    setCurrentEquipment(equipment);
                });

                markersRef.current[equipment.id] = marker;
            });

            // Set current equipment if not set
            if (!currentEquipment && equipmentData.length > 0) {
                setCurrentEquipment(equipmentData[0]);
            }

            // Fit bounds to show all markers
            const validMarkers = equipmentData.filter(eq =>
                eq && eq.lat && eq.lng && !isNaN(eq.lat) && !isNaN(eq.lng)
            );

            if (validMarkers.length > 0) {
                const bounds = L.latLngBounds(validMarkers.map(eq => [eq.lat, eq.lng]));
                mapInstance.current.fitBounds(bounds, {
                    padding: [50, 50],
                    maxZoom: 10
                });

                // Set center to first equipment
                const firstEquipment = validMarkers[0];
                if (firstEquipment) {
                    mapInstance.current.setView([firstEquipment.lat, firstEquipment.lng], 13);
                }
            }

        } catch (error) {
            console.error("Error updating map markers:", error);
        }
    }, [equipmentData, mapReady, createCustomIcon, currentEquipment]);

    // Update current equipment when device data changes
    useEffect(() => {
        if (deviceData && !currentEquipment) {
            const processedData = processEquipmentData(deviceData);
            if (processedData) {
                setCurrentEquipment(processedData);
            }
        }
    }, [deviceData, currentEquipment, processEquipmentData]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
            markersRef.current = {};
        };
    }, []);

    const renderEquipmentDetails = () => {
        if (!currentEquipment) return null;

        const status = calculateStatus(currentEquipment);
        const statusColor = getStatusColor(status);

        return (
            <table className="table table-hover table-bordered  table-responsive bg-white  ">
                <tbody>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>IMEI</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.id}
                        </td>
                    </tr>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>Ignition</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.ignition ? 'ON' : 'OFF'}
                        </td>
                    </tr>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>Speed</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.speed} km/h
                        </td>
                    </tr>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>Direction</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.direction}°
                        </td>
                    </tr>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>Engine</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.engineStatus ? 'ON' : 'OFF'}
                        </td>
                    </tr>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>Last Seen</td>
                        <td className="fs14 fw500" >
                            {moment(currentEquipment.lastSeen).format('DD-MMM-YYYY HH:mm:ss')}
                        </td>
                    </tr>
                     <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>External</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.externalBattery} V
                        </td>
                    </tr>
                    <tr>
                        <td className="fs14 fw-normal" style={{ "width": "33%" }}>Internal</td>
                        <td className="fs14 fw500" >
                            {currentEquipment.internalBattery} V

                        </td>
                    </tr>
                </tbody>
            </table>

        );
    };

    const handleRefresh = () => {
        if (data?.iot_device_imei) {
            fetchDeviceData(data.iot_device_imei);
        }
    };

    return (
        <div className="card pb-3">
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-8">
                        <h6 className="fw-bold primary_color mb-0">Asset Location & Details</h6>
                    </div>
                    <div className="col-4 text-end">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <TapIcon.FontAwesomeIcon icon={TapIcon.faSyncAlt} className={loading ? "fa-spin" : ""} />
                        </button>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-5">
                        {loading ? (
                            <div className="text-center py-4">
                                <Loader />
                            </div>
                        ) : (
                            renderEquipmentDetails()
                        )}
                    </div>

                    <div className="col-sm-7">
                        <div className="map-container" style={{
                            height: "275px",
                            position: "relative",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "1px solid #dee2e6"
                        }}>
                            <div
                                ref={mapRef}
                                style={{ width: "100%", height: "100%" }}
                            />

                            {!mapReady && (
                                <div className="map-loading-overlay">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden"><Loader /></span>
                                    </div>
                                    <p className="mt-2"><Loader /></p>
                                </div>
                            )}

                            {mapReady && equipmentData.length === 0 && !loading && (
                                <div className="map-loading-overlay">
                                    <p className="text-muted">No location data available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

          
        </div>
    );
}